import { WEBBED_NOTES_COPY, WebbedHotelNotes as WebbedHotelNotesData } from "../../../utils/booking/hotel/webbedHotelNotes";
import styles from "./webbedHotelNotes.module.scss";

interface iProps {
  notes: WebbedHotelNotesData | null;
  /** Show the hotel name above the block — only useful when several stays are listed together. */
  showHotelName?: boolean;
  className?: string;
}

/**
 * WebBeds' mandatory post-booking disclosures for one stay: the non-included
 * property fee and the rate's tariff notes. Rendered INLINE (not behind a click)
 * because WebBeds requires them to be displayed, and as TEXT only — the notes are
 * supplier free-text, already flattened to lines by parseHotelExtraInfo.
 *
 * Renders nothing for a non-WebBeds stay or one with nothing to disclose
 * (`getWebbedHotelNotes` returns null), so call-sites can mount it unconditionally.
 */
function WebbedHotelNotes({ notes, showHotelName = false, className = "" }: iProps): JSX.Element | null {
  if (!notes) return null;
  const { hotelName, tariffNotes, propertyFeeText } = notes;

  return (
    <div className={`${styles.container} ${className}`} onClick={(e) => e.stopPropagation()}>
      {showHotelName && hotelName && <p className={styles.hotelName}>{hotelName}</p>}
      {!!propertyFeeText && (
        <div className={styles.feeRow}>
          <div className={styles.feeLabelGroup}>
            <p className={styles.heading}>{WEBBED_NOTES_COPY.propertyFeeLabel}</p>
            <p className={styles.hint}>{WEBBED_NOTES_COPY.propertyFeeHint}</p>
          </div>
          <p className={styles.feeAmount}>{propertyFeeText}</p>
        </div>
      )}
      {tariffNotes.length > 0 && (
        <div className={styles.tariffNotes}>
          <p className={styles.heading}>{WEBBED_NOTES_COPY.tariffNotesLabel}</p>
          {tariffNotes.map((section, sectionIndex) => (
            <div className={styles.section} key={`${section.heading}-${sectionIndex}`}>
              {section.heading && <p className={styles.sectionHeading}>{section.heading}</p>}
              {section.lines.map((line, lineIndex) => (
                <p className={`${styles.line} ${line.bullet ? styles.bulletLine : ""}`} key={`${line.text}-${lineIndex}`}>
                  {line.text}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WebbedHotelNotes;
